import { useEffect, useMemo, useState } from 'react';

import { matchTraceToReplies, traceSince } from './engine-trace-match';
import { requestEngineTrace, type EngineRequest } from './validate-api';
import type { ValidateTurn } from './validate-runs';

/**
 * Is `/ai/validate/engine-trace` open here? Asked once per page load — it's
 * localhost-only for now, and a deployment says so on `/ai/health`.
 */
let available: Promise<boolean> | null = null;
const traceAvailable = () => {
  available ??= fetch('/ai/health', { credentials: 'include' })
    .then((r) => r.json())
    .then(
      (j: { capabilities?: { engineTrace?: boolean } }) =>
        j.capabilities?.engineTrace === true
    )
    .catch(() => false);
  return available;
};

/** Whether engine traces can be read here (localhost for now). */
export const useTraceAvailable = (): boolean => {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    let live = true;
    traceAvailable().then((v) => live && setOk(v));
    return () => {
      live = false;
    };
  }, []);
  return ok;
};

/**
 * Engine logs land anywhere from a few seconds to most of a minute after the
 * reply they describe (seen live: the greeting's lines were in, the next
 * reply's not yet, 9s on). So each new reply is looked up on this schedule,
 * stopping as soon as that reply has its trace.
 */
// The first lookup is right away, so a saved run shows its trace on opening.
const LOOKUPS_MS = [300, 3_000, 10_000, 25_000, 60_000];

/**
 * The decision and action nodes the bot engine walked for a chat, per bot
 * reply, read from logs.main. Nothing is fetched where the route is closed,
 * without a chat id, or before there's a reply.
 */
export const useEngineTrace = (
  chatId: string | null | undefined,
  transcript: ValidateTurn[]
) => {
  const [open, setOpen] = useState(false);
  const [requests, setRequests] = useState<EngineRequest[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  /** Still looking for the latest reply's trace — say so, not nothing. */
  const [searching, setSearching] = useState(false);
  const replies = transcript.filter((t) => t.role === 'bot').length;

  useEffect(() => {
    let live = true;
    traceAvailable().then((ok) => live && setOpen(ok));
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    setRequests(null);
    setProblem(null);
  }, [chatId]);

  useEffect(() => {
    if (!open || !chatId || !replies) return undefined;
    const ctrl = new AbortController();
    const sinceMs = traceSince(transcript);
    const lastReply = transcript.map((t) => t.role).lastIndexOf('bot');
    let timers: ReturnType<typeof setTimeout>[] = [];
    setSearching(true);
    const load = (final: boolean) =>
      requestEngineTrace({ chatId, sinceMs }, ctrl.signal)
        .then((r) => {
          setRequests(r.requests);
          setProblem(null);
          if (matchTraceToReplies(r.requests, transcript)[String(lastReply)]) {
            timers.forEach(clearTimeout);
            setSearching(false);
          } else if (final) {
            setSearching(false);
          }
        })
        .catch((err) => {
          if (!ctrl.signal.aborted) {
            timers.forEach(clearTimeout);
            setSearching(false);
            setProblem(err instanceof Error ? err.message : String(err));
          }
        });
    timers = LOOKUPS_MS.map((ms, i) =>
      setTimeout(() => load(i === LOOKUPS_MS.length - 1), ms)
    );
    return () => {
      ctrl.abort();
      timers.forEach(clearTimeout);
    };
    // Keyed on the number of replies, not the transcript's identity: a new
    // reply is what makes a new lookup worth doing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, chatId, replies]);

  const byReply = useMemo(
    () => (requests ? matchTraceToReplies(requests, transcript) : {}),
    [requests, transcript]
  );

  return {
    enabled: open && !!chatId,
    byReply,
    problem,
    searching,
    requests: requests ?? [],
  };
};
